import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    TextField,
    FormHelperText,
    Button,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import { useTranslate, useInput, useLocaleState } from 'react-admin';
import { Translate, ContentCopy, SwapHoriz } from '@mui/icons-material';
import * as OpenCC from 'opencc-js';
import { 
    getLocalizedAddressData,
    AddressDistrict,
    AddressLocality 
} from '../../data/hongKongAddressData';

interface MultiLanguageFieldsProps {
    fields: {
        name: string;
        label: string;
        multiline?: boolean;
        rows?: number;
        required?: boolean;
        helperText?: string;
    }[];
    languages?: { code: string; name: string }[];
    formData?: any;
}

export const MultiLanguageFields: React.FC<MultiLanguageFieldsProps> = ({
    fields,
    languages = [
        { code: 'en', name: 'English' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'zh-CN', name: '简体中文' }
    ],
    formData
}) => {
    const [currentLang, setCurrentLang] = useState(0);
    const translate = useTranslate();
    const translationsInput = useInput({ source: 'translations' });
    
    // State for Hong Kong address dropdowns
    const [selectedRegionKey, setSelectedRegionKey] = useState<string>('');
    const [districts, setDistricts] = useState<AddressDistrict[]>([]);
    const [localities, setLocalities] = useState<AddressLocality[]>([]);
    
    const currentLanguageCode = languages[currentLang].code;
    const addressData = useMemo(() => 
        getLocalizedAddressData(currentLanguageCode), 
        [currentLanguageCode]
    );
    
    // Default to Hong Kong if country is not set (for new sites) or explicitly set to Hong Kong
    const isHongKong = !formData?.country || formData?.country === 'Hong Kong' || formData?.country === 'HK';

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentLang(newValue);
        // Clear district/locality states to force re-initialization with new language
        setSelectedRegionKey('');
        setDistricts([]);
        setLocalities([]);
    };

    // Get translations from form data
    const translations = React.useMemo(() => 
        translationsInput.field.value || [], 
        [translationsInput.field.value]
    );
    const currentTranslation = React.useMemo(() => 
        translations[currentLang] || {}, 
        [translations, currentLang]
    );

    // Initialize translations array if empty
    React.useEffect(() => {
        if (translations.length === 0) {
            const initialTranslations = languages.map(lang => ({
                language: lang.code,
                ...fields.reduce((acc, field) => {
                    // Initialize country field for Hong Kong sites
                    if (field.name === 'country' && isHongKong) {
                        const countryName = lang.code === 'zh-TW' ? '香港' :
                                          lang.code === 'zh-CN' ? '香港' :
                                          'Hong Kong';
                        return { ...acc, [field.name]: countryName };
                    }
                    return { ...acc, [field.name]: '' };
                }, {})
            }));
            translationsInput.field.onChange(initialTranslations);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount
    
    // Remove the problematic auto-update effect entirely
    
    // Initialize Hong Kong address dropdowns when translation values change or language changes
    useEffect(() => {
        if (!isHongKong || !currentTranslation.state || !addressData.length) return;
        
        // Find the region by the district key
        for (const region of addressData) {
            const district = region.districts.find(d => d.key === currentTranslation.state);
            if (district) {
                // Only update if values have actually changed (using key comparison)
                if (selectedRegionKey !== region.key) {
                    setSelectedRegionKey(region.key);
                }
                // Only update districts if the region has changed
                if (selectedRegionKey !== region.key || districts.length === 0) {
                    setDistricts(region.districts);
                }
                // Only update localities if needed
                if (currentTranslation.city && (selectedRegionKey !== region.key || localities.length === 0)) {
                    setLocalities(district.localities);
                }
                break;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTranslation.state, isHongKong, currentLang]); // Remove addressData to prevent loops

    const handleFieldChange = (fieldName: string, value: string) => {
        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }
        updatedTranslations[currentLang][fieldName] = value;
        translationsInput.field.onChange(updatedTranslations);
    };
    
    const handleRegionChange = (value: string) => {
        setSelectedRegionKey(value);
        const region = addressData.find(r => r.key === value);
        if (region) {
            setDistricts(region.districts);
            setLocalities([]);
            // Clear city and state in the current translation
            handleFieldChange('city', '');
            handleFieldChange('state', '');
        }
    };
    
    const handleDistrictChange = (value: string) => {
        const district = districts.find(d => d.key === value);
        if (district) {
            setLocalities(district.localities);
            // Clear city when district changes
            handleFieldChange('city', '');
            // Update state with district key
            handleFieldChange('state', value);
        }
    };

    const handleCopyFromBasic = () => {
        if (!formData) return;

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        const targetLanguage = languages[currentLang].code;

        // Copy values from basic information for each field
        fields.forEach(field => {
            if (formData[field.name] !== undefined) {
                let value = formData[field.name] || '';

                // Handle address field translations
                if (formData.country === 'Hong Kong') {
                    if (field.name === 'city' || field.name === 'state') {
                        // Copy the key value directly - it's already a key from the Basic Information
                        // No translation needed as dropdowns will display the localized name
                        updatedTranslations[currentLang][field.name] = value;
                    } else if (field.name === 'country') {
                        // Translate country name
                        value = targetLanguage === 'zh-TW' ? '香港' :
                               targetLanguage === 'zh-CN' ? '香港' :
                               'Hong Kong';
                        updatedTranslations[currentLang][field.name] = value;
                    } else {
                        // For other fields, copy as-is
                        updatedTranslations[currentLang][field.name] = value;
                    }
                } else {
                    // For non-Hong Kong addresses, copy as-is
                    updatedTranslations[currentLang][field.name] = value;
                }
            }
        });

        // Update translations in a single call
        translationsInput.field.onChange(updatedTranslations);

        // If it's Hong Kong and we have state/city values, update the dropdowns after a small delay
        if (formData.country === 'Hong Kong' && formData.state) {
            setTimeout(() => {
                // Find the region by the district key
                for (const region of addressData) {
                    const district = region.districts.find(d => d.key === formData.state);
                    if (district) {
                        setSelectedRegionKey(region.key);
                        setDistricts(region.districts);
                        if (formData.city) {
                            setLocalities(district.localities);
                        }
                        break;
                    }
                }
            }, 100);
        }
    };

    const handleTranslateFromSimplified = () => {
        // Find the simplified Chinese translation (zh-CN)
        const simplifiedIndex = languages.findIndex(lang => lang.code === 'zh-CN');
        const simplifiedTranslation = translations[simplifiedIndex];

        if (!simplifiedTranslation) {
            return;
        }

        // Create converter from Simplified to Traditional
        const converter = OpenCC.Converter({ from: 'cn', to: 'tw' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from simplified to traditional
        fields.forEach(field => {
            const simplifiedValue = simplifiedTranslation[field.name];
            if (simplifiedValue && typeof simplifiedValue === 'string') {
                // Skip address keys (they are already keys, not text to translate)
                if (isHongKong && (field.name === 'city' || field.name === 'state')) {
                    updatedTranslations[currentLang][field.name] = simplifiedValue;
                } else if (field.name === 'country' && simplifiedValue === '香港') {
                    // Keep Hong Kong as-is (it's the same in both Traditional and Simplified)
                    updatedTranslations[currentLang][field.name] = '香港';
                } else {
                    // Convert the text
                    updatedTranslations[currentLang][field.name] = converter(simplifiedValue);
                }
            }
        });

        translationsInput.field.onChange(updatedTranslations);

        // Update dropdowns if needed for Hong Kong addresses
        if (isHongKong && simplifiedTranslation.state) {
            setTimeout(() => {
                for (const region of addressData) {
                    const district = region.districts.find(d => d.key === simplifiedTranslation.state);
                    if (district) {
                        setSelectedRegionKey(region.key);
                        setDistricts(region.districts);
                        if (simplifiedTranslation.city) {
                            setLocalities(district.localities);
                        }
                        break;
                    }
                }
            }, 100);
        }
    };

    const handleTranslateFromTraditional = () => {
        // Find the traditional Chinese translation (zh-TW)
        const traditionalIndex = languages.findIndex(lang => lang.code === 'zh-TW');
        const traditionalTranslation = translations[traditionalIndex];

        if (!traditionalTranslation) {
            return;
        }

        // Create converter from Traditional to Simplified
        const converter = OpenCC.Converter({ from: 'tw', to: 'cn' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from traditional to simplified
        fields.forEach(field => {
            const traditionalValue = traditionalTranslation[field.name];
            if (traditionalValue && typeof traditionalValue === 'string') {
                // Skip address keys (they are already keys, not text to translate)
                if (isHongKong && (field.name === 'city' || field.name === 'state')) {
                    updatedTranslations[currentLang][field.name] = traditionalValue;
                } else if (field.name === 'country' && traditionalValue === '香港') {
                    // Keep Hong Kong as-is (it's the same in both Traditional and Simplified)
                    updatedTranslations[currentLang][field.name] = '香港';
                } else {
                    // Convert the text
                    updatedTranslations[currentLang][field.name] = converter(traditionalValue);
                }
            }
        });

        translationsInput.field.onChange(updatedTranslations);

        // Update dropdowns if needed for Hong Kong addresses
        if (isHongKong && traditionalTranslation.state) {
            setTimeout(() => {
                for (const region of addressData) {
                    const district = region.districts.find(d => d.key === traditionalTranslation.state);
                    if (district) {
                        setSelectedRegionKey(region.key);
                        setDistricts(region.districts);
                        if (traditionalTranslation.city) {
                            setLocalities(district.localities);
                        }
                        break;
                    }
                }
            }, 100);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Translate sx={{ mr: 1, color: '#00C853' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translate('resources.sites.multilang.title')}
                </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.sites.multilang.helperText')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
                <Tabs
                    value={currentLang}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                        }
                    }}
                >
                    {languages.map((lang) => (
                        <Tab key={lang.code} label={lang.name} />
                    ))}
                </Tabs>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyFromBasic}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#00C853',
                            color: '#00C853',
                            '&:hover': {
                                borderColor: '#00A843',
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        {translate('resources.sites.multilang.copyFromBasic')}
                    </Button>

                    {/* Show translation button for Traditional Chinese tab */}
                    {languages[currentLang].code === 'zh-TW' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromSimplified}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            從簡體中文翻譯
                        </Button>
                    )}

                    {/* Show translation button for Simplified Chinese tab */}
                    {languages[currentLang].code === 'zh-CN' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromTraditional}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            从繁体中文翻译
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fields.map((field) => {
                    // Special handling for Hong Kong address fields
                    if (isHongKong && field.name === 'state') {
                        return (
                            <Box key={field.name} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                                    <InputLabel>{translate('resources.sites.fields.region')}</InputLabel>
                                    <Select
                                        value={selectedRegionKey}
                                        onChange={(e) => handleRegionChange(e.target.value as string)}
                                        label={translate('resources.sites.fields.region')}
                                        sx={{ height: '56px' }}
                                    >
                                        {addressData.map(region => (
                                            <MenuItem key={region.key} value={region.key}>
                                                {region.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                
                                <FormControl fullWidth sx={{ flex: 1, minWidth: 0 }}>
                                    <InputLabel>{translate('resources.sites.fields.state')}</InputLabel>
                                    <Select
                                        value={districts.some(d => d.key === currentTranslation[field.name]) ? currentTranslation[field.name] : ''}
                                        onChange={(e) => handleDistrictChange(e.target.value as string)}
                                        label={translate('resources.sites.fields.state')}
                                        disabled={!selectedRegionKey}
                                        sx={{ height: '56px' }}
                                    >
                                        {districts.map(district => (
                                            <MenuItem key={district.key} value={district.key}>
                                                {district.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        );
                    }
                    
                    if (isHongKong && field.name === 'city') {
                        return (
                            <FormControl key={field.name} fullWidth>
                                <InputLabel>{translate('resources.sites.fields.city')}</InputLabel>
                                <Select
                                    value={localities.some(l => l.key === currentTranslation[field.name]) ? currentTranslation[field.name] : ''}
                                    onChange={(e) => handleFieldChange(field.name, e.target.value as string)}
                                    label={translate('resources.sites.fields.city')}
                                    disabled={!currentTranslation.state}
                                    sx={{ height: '56px' }}
                                >
                                    {localities.map(locality => (
                                        <MenuItem key={locality.key} value={locality.key}>
                                            {locality.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        );
                    }
                    
                    if (isHongKong && field.name === 'country') {
                        // For Hong Kong, always show the localized country name
                        const displayValue = languages[currentLang].code === 'zh-TW' ? '香港' :
                                           languages[currentLang].code === 'zh-CN' ? '香港' :
                                           'Hong Kong';
                        // But store the actual value from the translation state
                        const storedValue = currentTranslation[field.name] || displayValue;
                        
                        return (
                            <TextField
                                key={field.name}
                                fullWidth
                                label={field.label}
                                value={storedValue}
                                disabled
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            />
                        );
                    }
                    
                    // Default TextField for other fields
                    return (
                        <Box key={field.name}>
                            <TextField
                                fullWidth
                                label={field.label}
                                value={currentTranslation[field.name] || ''}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                multiline={field.multiline}
                                rows={field.rows}
                                required={field.required && languages[currentLang].code === 'en'}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#FAFAFA',
                                        '&:hover': {
                                            backgroundColor: '#F5F5F5',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#FFFFFF',
                                        },
                                    },
                                }}
                            />
                            {field.helperText && (
                                <FormHelperText>{field.helperText}</FormHelperText>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {languages[currentLang].code !== 'en' && (
                <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: '#E8F5E9', 
                    borderRadius: 1,
                    border: '1px solid #C8E6C9'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        {translate('resources.sites.multilang.nonEnglishNote')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};